import React, { useState, useEffect } from 'react';
import { Table, Card, Input, Select, Space, Tag, Button, Typography, Row, Col } from 'antd';
import type { TableProps } from 'antd';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import { getWarehouseList } from '../../api/warehouse';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../contexts/PermissionContext';
import { useTableQuery } from '../../hooks/useTableQuery';
import type { Warehouse } from '../../types/warehouse';
import { EyeOutlined, EditOutlined } from '@ant-design/icons';

const { Title } = Typography;
const { Option } = Select;

// 定义筛选条件类型
interface WarehouseFilters {
  code: string;
  country: string | undefined;
}

const WarehouseList: React.FC = () => {
  const navigate = useNavigate();
  const { isAdmin, isHQStaff, isWarehouseSupervisor, isWarehouseStaff, isMainAccount, isSubAccount } = usePermissions();

  // 使用 useTableQuery Hook - 实现"单一数据源 + 强制同步 + 解耦触发"架构
  const {
    state,
    data,
    loading,
    error,
    actions,
    paginationConfig
  } = useTableQuery<WarehouseFilters>({
    initialState: {
      pagination: { current: 1, pageSize: 10, total: 0 },
      filters: { code: '', country: undefined },
      sorting: { field: 'created_at', order: 'desc' }
    },
    urlConfig: {
      syncToUrl: true,           // 启用URL状态同步
      urlKeys: ['pagination', 'filters', 'sorting']  // 同步分页、筛选、排序到URL
    },
    fetchData: async (state) => {
      const { pagination, filters, sorting } = state;
      
      console.log('🔍 fetchData 原始状态:', { pagination, filters, sorting });
      
      // 构建API参数，确保筛选条件正确传递
      const params: any = {
        page: pagination.current,
        page_size: pagination.pageSize,
        ordering: sorting.order === 'desc' ? `-${sorting.field}` : sorting.field,
      };
      
      // 只添加有值的筛选条件
      if (filters.code && filters.code.trim()) {
        params.code = filters.code.trim();
      }
      
      if (filters.country && filters.country.trim()) {
        params.country = filters.country.trim();
      }
      
      console.log('🔍 fetchData 构建的API参数:', params);
      console.log('🔍 筛选条件详情:', {
        code: filters.code,
        country: filters.country,
        codeType: typeof filters.code,
        countryType: typeof filters.country,
        hasCode: !!filters.code,
        hasCountry: !!filters.country
      });
      
      // 调用API
      const response = await getWarehouseList(params);
      
      console.log('📊 API响应:', response);
      
      return {
        results: response.data.results,
        count: response.data.count
      };
    }
  });

  // 处理筛选条件变化
  const handleFilterChange = (key: keyof WarehouseFilters, value: any) => {
    console.log('🔍 筛选条件变化:', { key, value, type: typeof value });
    
    // 更新筛选条件
    const newFilters = { [key]: value };
    console.log('🔍 新的筛选条件:', newFilters);
    
    // 更新状态
    actions.updateFilters(newFilters);
    
    // 手动触发搜索，确保数据获取
    console.log('🔍 手动触发搜索');
  };

  // 处理搜索
  const handleSearch = () => {
    console.log('🔍 执行搜索');
    // 搜索会自动触发，因为filters变化会触发fetchData
  };

  // 重置筛选条件
  const handleReset = () => {
    console.log('🔄 重置筛选条件');
    actions.reset();
  };

  // 表格变化处理（分页、排序等）
  const handleTableChange: TableProps<Warehouse>['onChange'] = (
    pagination,
    filters,
    sorter,
  ) => {
    console.log('📊 表格变化:', { pagination, filters, sorter });
    
    // 处理分页变化
    if (pagination.current !== state.pagination.current) {
      console.log('📄 页码变化:', pagination.current, '->', state.pagination.current);
      actions.updatePagination({ current: pagination.current });
    }
    
    if (pagination.pageSize !== state.pagination.pageSize) {
      console.log('📏 每页数量变化:', pagination.pageSize, '->', state.pagination.pageSize);
      actions.updatePagination({ current: 1, pageSize: pagination.pageSize });
    }
    
    // 处理排序变化（如果需要的话）
    if (sorter && !Array.isArray(sorter) && sorter.field) {
      console.log('排序变化:', sorter);
      actions.updateSorting({
        field: String(sorter.field),
        order: sorter.order === 'ascend' ? 'asc' : 'desc'
      });
    }
  };

  // 查看详情
  const handleViewDetail = (record: Warehouse) => {
    // 基于用户角色决定跳转前缀
    let basePath = '/dashboard/customer/warehouses';
    if (isAdmin()) basePath = '/dashboard/admin/warehouses';
    else if (isHQStaff() || isWarehouseSupervisor() || isWarehouseStaff()) basePath = '/dashboard/staff/warehouses';
    else if (isMainAccount() || isSubAccount()) basePath = '/dashboard/customer/warehouses';
    const targetPath = `${basePath}/${record.id}`;
    try {
      if (record?.name) {
        sessionStorage.setItem(`tab_title_warehouse:${targetPath}`, record.name);
      }
    } catch {}
    navigate(targetPath);
  };

  const handleEdit = (record: Warehouse) => {
    if (!isAdmin()) return;
    navigate(`/dashboard/admin/warehouses/${record.id}/edit`);
  };

  // 表格列定义（取消排序功能）
  const columns: TableProps<Warehouse>['columns'] = [
    {
      title: '仓库名称',
      dataIndex: 'name',
      key: 'name',
      width: 150,
      render: (text: string) => text,
    },
    {
      title: '仓库代码',
      dataIndex: 'code',
      key: 'code',
      width: 120,
    },
    {
      title: '所在国家',
      dataIndex: 'country_display',
      key: 'country',
      width: 100,
    },
    {
      title: '城市',
      dataIndex: 'city',
      key: 'city',
      width: 100,
    },
    {
      title: '联系人',
      dataIndex: 'contact_name',
      key: 'contact_name',
      width: 100,
    },
    {
      title: '联系电话',
      dataIndex: 'contact_phone',
      key: 'contact_phone',
      width: 120,
    },
    {
      title: '入库方式',
      dataIndex: 'inbound_methods_display',
      key: 'inbound_methods',
      width: 180,
      render: (text: string) => (
        <Space>
          {text.split(', ').map((method, index) => (
            <Tag key={index} color="blue">{method}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: '出库方式',
      dataIndex: 'outbound_methods_display',
      key: 'outbound_methods',
      width: 180,
      render: (text: string) => (
        <Space>
          {text.split(', ').map((method, index) => (
            <Tag key={index} color="green">{method}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: '卸货平台',
      dataIndex: 'has_loading_dock',
      key: 'has_loading_dock',
      width: 100,
      render: (hasLoadingDock: boolean) => (
        <Tag color={hasLoadingDock ? 'success' : 'default'}>
          {hasLoadingDock ? '有' : '无'}
        </Tag>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 180,
      fixed: 'right' as const,
      render: (_, record: Warehouse) => (
        <Space size={4}>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
          >
            查看
          </Button>
          {isAdmin() && (
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            >
              编辑
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="warehouse-list">
      <Card>
        {/* 页面标题和统计信息 */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <Title level={4} style={{ margin: 0 }}>我的仓库</Title>
              <div className="flex items-center space-x-2">
                <Tag color="blue" style={{ fontSize: '14px', padding: '8px' }}>
                  共 {state.pagination.total} 条记录
                </Tag>
              </div>
            </div>
          </div>
        </div>

        {/* 筛选区域 */}
        <div style={{ marginBottom: 16 }}>
          <Row gutter={16} align="middle">
            <Col span={6}>
              <Space>
                <span style={{ fontWeight: 500, color: '#333' }}>仓库代码：</span>
                <Input
                  placeholder="请输入仓库代码"
                  value={state.filters.code}
                  onChange={(e) => {
                    console.log('🏷️ 仓库代码变化:', { value: e.target.value, type: typeof e.target.value });
                    handleFilterChange('code', e.target.value);
                  }}
                  allowClear
                  style={{ width: 150 }}
                />
              </Space>
            </Col>
            <Col span={6}>
              <Space>
                <span style={{ fontWeight: 500, color: '#333' }}>国家：</span>
                <Select
                  placeholder="请选择国家"
                  value={state.filters.country}
                  onChange={(value) => {
                    console.log('🌍 国家选择变化:', { value, type: typeof value });
                    handleFilterChange('country', value);
                  }}
                  allowClear
                  style={{ width: 150 }}
                >
                  <Option value="CN">中国大陆</Option>
                  <Option value="HK">香港</Option>
                  <Option value="US">美国</Option>
                </Select>
              </Space>
            </Col>
            <Col span={6}>
              <Space>
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={handleSearch}
                  loading={loading}
                >
                  搜索
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleReset}
                >
                  重置
                </Button>
              </Space>
            </Col>
          </Row>
        </div>

        {/* 数据表格 */}
        <Card>
          <Table<Warehouse>
            columns={columns}
            dataSource={data}
            rowKey="id"
            loading={loading}
            pagination={paginationConfig}
            onChange={handleTableChange}
            scroll={{ x: 1500 }}
            size="middle"
          />
        </Card>
      </Card>
    </div>
  );
};

export default WarehouseList; 